import React, { useState, useEffect, useRef } from 'react';
import { View, Text, Button, Modal, Alert, ScrollView, TouchableOpacity, Image, Pressable } from 'react-native';
import { colors, styles } from './components/AppStyles';
import BottomNavBar from './BottomNavBar';
import { HomeButton } from './components/StyledButtons.js';
import AsyncStorage from '@react-native-async-storage/async-storage';
import screenGroups from './real-data.json';
import { CounterRatiosContext } from './CounterRatiosContext';
import { setIsResetButtonPressedValue } from './ResetButtonState';

/**
 * HomeScreen Component
 * 
 * This component serves as the home page for the application.
 * It displays a list of screen groups and their progress statuses.
 * A user can navigate to a group's screen, or reset their progress.
 * Upon selecting a new group, a modal prompts the user to choose between viewing
 * the term or the definition first.
 * 
 * @param {Object} navigation - The navigation object from React Navigation.
 * @param {Object} route - The route object from React Navigation.
 * @returns {JSX.Element}
 */
const HomeScreen = ({ navigation, route }) => {
    const [isModalVisible, setModalVisible] = useState(false); // Control visibility of the choice modal
    const [selectedGroup, setSelectedGroup] = useState(null); // Store the currently selected screen group
    const { counterRatios, setCounterRatios } = React.useContext(CounterRatiosContext); // Access state for group progress
    const buttonColors = ['#e8f5ff', '#fbf1e2', '#e8f5ff', '#fbf1e2']; // Alternating background colors for group buttons
    const swipeDirectionHistoryRef = useRef([]); // Define swipeDirectionHistoryRef as a useRef

    /**
     * Navigate to the screens of a group.
     * Updates progress status and navigates based on user's choice.
     * 
     * @param {Object} group - The selected group object.
     * @param {string} choice - The user's choice ("title" or "definition").
     */
    const navigateToGroup = async (group, choice) => {
        if (!group) return;
      
        const existingData = counterRatios[group.groupId] || {};
        //console.log("navigateToGroup: ", existingData);
        const isCompleted = typeof existingData.percentage !== "undefined" && existingData.percentage !== null && existingData.percentage !== 0;
        const startScreenIndex = (existingData.lastScreenIndex && !isCompleted) ? existingData.lastScreenIndex : 0;
      
        let updatedData = {
          ...existingData,
          choice: choice,
        };
      
        // Reset the data only when the group has been fully navigated
        if (isCompleted) {
            updatedData = {
                lastScreenIndex: null,
                counter1: 0,
                counter2: 0,
                percentage: null,
                choice: choice,
            };
            // Save the data to in-memory state and AsyncStorage
            setCounterRatios({
                ...counterRatios,
                [group.groupId]: updatedData,
            });

            try {
                // Define the keys for storing progress and group state data
                const groupStateKey = `groupState_${group.groupId}`;

                // Save the data to AsyncStorage
                await AsyncStorage.setItem(groupStateKey, JSON.stringify(updatedData));
            } catch (error) {
                //console.error("Failed to save data in AsyncStorage:", error);
            }
        }
        else {
            // Save the data to in-memory state and AsyncStorage
            setCounterRatios({
                ...counterRatios,
                [group.groupId]: updatedData,
            });
        }
      
        navigation.navigate(group.groupId, {
          screens: group.screens,
          currentIndex: startScreenIndex,
          choice,
        });
      
        setModalVisible(false);
      };               

    // Set the header right button to reset progress--
    React.useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => (
                <TouchableOpacity 
                    onPress={handleResetPress} 
                    style={styles.resetButton} 
                    activeOpacity={0.6}
                >
                    <Text style={styles.resetButtonText}>&#8634; Reset</Text>
                </TouchableOpacity>
            )
        });
    }, [navigation]);

    /**
     * Handle reset button press.
     * Shows an alert for confirmation before resetting progress data.
     *
     */
    const handleResetPress = () => {
        Alert.alert(
            "Reset Progress",
            "Are you sure you want to reset all your progress?",
            [
                {
                    text: "Cancel",
                    style: "cancel"
                },
                {
                    text: "Yes, Reset",
                    onPress: () => {
                        setIsResetButtonPressedValue(true); // Set the flag when the reset button is pressed
                        resetAllData(); // Call resetAllData function
                    }
                }
            ]
        );
    };

    /**
     * Reset all progress data.
     * Resets both in-memory and stored counterRatios data and swipeHistory data.
     */
    const resetAllData = async () => {
        setCounterRatios({});
        AsyncStorage.setItem('counterRatios', JSON.stringify({}));
        try {
            // Remove the swipeHistory data for each group
            for (const group of screenGroups) {
                const swipeHistoryKey = `swipeHistory_${group.groupId}`;
                const groupStateKey = `groupState_${group.groupId}`;
                const progressDataKey = `progressData_${group.groupId}`
                await AsyncStorage.removeItem(swipeHistoryKey);
                await AsyncStorage.removeItem(groupStateKey);
                await AsyncStorage.removeItem(progressDataKey);
            }
        } catch (error) {
            //console.error("Failed to remove swipe history:", error);
        }
    };

    useEffect(() => {
        // Load progress data and saved state data for each group
        for (const group of screenGroups) {
          loadSavedGroupState(group);
        }
      }, []);     
      
      const loadSavedGroupState = async (group) => {
        try {
          const groupStateKey = `groupState_${group.groupId}`;
          const savedState = await AsyncStorage.getItem(groupStateKey);
          if (savedState) {
            const stateData = JSON.parse(savedState);
            // Update the state for the group
            setCounterRatios((prevRatios) => ({
              ...prevRatios,
              [group.groupId]: {
                choice: stateData.choice,
                counter1: stateData.counter1,
                counter2: stateData.counter2,
                lastScreenIndex: stateData.lastScreenIndex,
                swipeHistory: stateData.swipeHistory,
              },
            }));
            // Update swipe history
            swipeDirectionHistoryRef.current = stateData.swipeHistory || [];
            //console.log("loadSavedGroupState: ", groupStateKey, savedState);
          }
        } catch (error) {
          //console.error("Failed to load saved group state:", error);
        }
      };      

    return (
        // Root container for the entire HomeScreen
        <View style={styles.container}>
            {/* ScrollView containing the groups. Allows the list of groups to be scrollable */}
            <ScrollView contentContainerStyle={styles.contentContainer}>
                <Image style={styles.ccmcLogo} source={require('./assets/ccmc_logo.png')} />
                <Text style={styles.introText}>
                    Welcome to the Commission's NEW Glossary App! You have provided great feedback and this has enabled us to improve the app. Additionally, the glossary has been expanded to include key terms and definitions related to new content that has been added to the Case Management Body of Knowledge (CMBOK). The app allows for better clarity of definition and will offer a better user experience. Below you will find over 770 terms and definitions, divided into 26 decks with 30 terms.{'\n'}
                    {'\n'}
                    To begin, select a deck and start learning! You'll be presented with a term or definition. When you reveal the answer, click either the green or red button, depending on whether you feel you would have answered correctly. This helps you track your knowledge level for that deck. Once a deck has been completed, it will automatically reset when you start answering the questions again. As always, your feedback is welcomed.
                </Text>


                {/* Mapping over the screenGroups to render each group's status and progress */}
                {screenGroups.map((group, index) => {
                    const groupData = counterRatios[group.groupId] || {};
                    // if (Object.keys(groupData).length > 0) {
                    //     console.log("progressText: ", group.groupId, groupData);
                    // }
                    let progressText = "Status: Not Started";
                    let progressTextColor = colors.textProgressNotStarted;
                    if (groupData.percentage) {
                        progressText = `Status: Completed\nScore: ${groupData.percentage}%`;
                        progressTextColor = colors.textProgressCompleted;                        
                    } else if (groupData.lastScreenIndex != null && groupData.lastScreenIndex !== 0) {
                        //console.log(groupData);
                        progressText = `Status: In Progress`;
                        progressTextColor = colors.textProgressInProgress;
                    }
    
                    // Each group is rendered inside a container
                    return (
                        <View key={group.groupId} style={styles.groupContainer}>
    
                            {/* HomeButton represents each group. 
                                 It shows the group's title and progress.
                                 On pressing, it either navigates directly or shows a modal to make a choice */}
                            <HomeButton
                                index={index + 1}
                                title={group.groupId}
                                progressText={progressText}
                                progressTextColor={progressTextColor} // Use the imported color
                                onPress={() => {
                                    if (groupData.choice && (typeof groupData.percentage === "undefined" || groupData.percentage === null)) {
                                        navigateToGroup(group, groupData.choice);
                                    } else {
                                        setSelectedGroup(group);
                                        setModalVisible(true);
                                    }
                                }}                          
                                color={buttonColors[index % 4]}
                            />
                        </View>
                    );
                })}
            </ScrollView>
    
            {/* The navigation bar at the bottom of the screen */}
            <BottomNavBar navigation={navigation} current="Home" />
    
            {/* Conditional rendering of the modal to make a choice between "Term" and "Definition" */}
            {isModalVisible && (
                <Modal
                    transparent={true} // Makes the background transparent
                    animationType="slide" // Slide up animation when the modal appears
                    visible={isModalVisible} // Controls the visibility based on state
                >
                    {/* Outer container for modal, potentially for overlay styling */}
                    <View style={styles.modalOuterContainer}>
    
                        {/* Inner container for modal content */}
                        <View style={styles.modalInnerContainer}>
                            <Text style={styles.modalQuestion}>What would you like to see first?</Text>
    
                            <View style={styles.modalButtonWrapper}>
                                {/* Button to choose "Term". Navigates to the group's screens with "title" choice */}
                                {/* <Button title="Term" onPress={() => navigateToGroup(selectedGroup, "title")} /> */}
                                <Pressable style={styles.modalTerm}>
                                    <TouchableOpacity onPress={() => navigateToGroup(selectedGroup, "title")}>
                                        <Text style={styles.modalButtonText}>Term</Text>
                                    </TouchableOpacity>
                                </Pressable>
        
                                {/* Button to choose "Definition". Navigates to the group's screens with "definition" choice */}
                                {/* <Button title="Definition" onPress={() => navigateToGroup(selectedGroup, "definition")} /> */}
                                <Pressable style={styles.modalDefinition}>
                                    <TouchableOpacity onPress={() => navigateToGroup(selectedGroup, "definition")}>
                                        <Text style={styles.modalButtonText}>Definition</Text>
                                    </TouchableOpacity>
                                </Pressable>
                            </View>

                             {/* Add Cancel button */}
                            {/* <Button title="Cancel" onPress={() => setModalVisible(false)} /> */}
                            
                            <Pressable style={styles.modalCancel}>
                                <TouchableOpacity onPress={() => setModalVisible(false)}>
                                    <Text style={styles.modalButtonText}>Cancel</Text>
                                </TouchableOpacity>
                            </Pressable>
                        </View>
                    </View>
                </Modal>
            )}
        </View>
    );    
};

export default HomeScreen;